import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api, setSessionToken } from './api';

const AuthCtx = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [sellerProfile, setSellerProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get('/auth/me');
      setUser(data.user);
      setSellerProfile(data.seller_profile || null);
      return data.user;
    } catch {
      setUser(null);
      setSellerProfile(null);
      return null;
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    fetchMe().finally(() => setLoading(false));
  }, [fetchMe]);

  const login = async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    setSessionToken(data.session_token);
    setUser(data.user);
    await fetchMe();
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post('/auth/register', payload);
    setSessionToken(data.session_token);
    setUser(data.user);
    await fetchMe();
    return data.user;
  };

  const googleExchange = async (session_id, role = 'buyer') => {
    const { data } = await api.post('/auth/google-session', { session_id, role });
    setSessionToken(data.session_token);
    setUser(data.user);
    await fetchMe();
    return data.user;
  };

  const appleSignIn = async (payload) => {
    const { data } = await api.post('/auth/apple', payload);
    setSessionToken(data.session_token);
    setUser(data.user);
    await fetchMe();
    return data.user;
  };

  const logout = async () => {
    try { await api.post('/auth/logout'); } catch { /* ignore */ }
    setSessionToken(null);
    setUser(null);
    setSellerProfile(null);
  };

  return (
    <AuthCtx.Provider value={{ user, sellerProfile, loading, login, register, logout, googleExchange, appleSignIn, refresh: fetchMe, refreshUser: fetchMe }}>
      {children}
    </AuthCtx.Provider>
  );
};

export const useAuth = () => useContext(AuthCtx);
